import { createTheme, alpha } from '@mui/material';
import TablePaginationActions from '../components/common/TablePaginationActions';

/**
 * Dynamically generates the MUI theme based on the centralized branding properties.
 * 
 * Supports:
 * - primaryColor
 * - secondaryColor
 * - accentColor
 * - backgroundColor
 * - textColor
 */
export const createAppTheme = (branding = {}) => {
    const primary = branding.primaryColor || '#4146e0';
    const secondary = branding.secondaryColor || '#3539bc';
    const accent = branding.accentColor || '#7a8cf4';
    const background = branding.backgroundColor || '#f8fafc';
    const textColor = branding.textColor || '#101828';

    return createTheme({
        palette: {
            mode: 'light',
            primary: {
                main: primary,
                dark: secondary,
                light: accent,
            },
            secondary: {
                main: secondary,
                dark: secondary,
                light: accent,
            },
            background: {
                default: background,
                paper: '#ffffff',
            },
            text: {
                primary: textColor,
                secondary: '#475467',
            },
        },
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            h1: { fontWeight: 800 },
            h2: { fontWeight: 800 },
            h3: { fontWeight: 700 },
            h4: { fontWeight: 700 },
            h5: { fontWeight: 600 },
            h6: { fontWeight: 600 },
            button: { textTransform: 'none', fontWeight: 600 },
        },
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                },
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        padding: '7px 16px',
                        minHeight: 38,
                        boxShadow: 'none',
                        fontFamily: '"Inter", sans-serif',
                    },
                    containedPrimary: {
                        backgroundColor: primary,
                        color: '#ffffff',
                        '&:hover': {
                            backgroundColor: secondary,
                            boxShadow: 'none',
                        },
                    },
                    containedSecondary: {
                        backgroundColor: secondary,
                        color: '#ffffff',
                        '&:hover': {
                            backgroundColor: primary,
                            boxShadow: 'none',
                        },
                    },
                    contained: {
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                    },
                    outlined: {
                        backgroundColor: '#ffffff',
                        borderColor: '#d0d5dd',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        color: '#344054',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#f8fafc',
                            borderColor: '#cbd5e1',
                            color: '#1e293b',
                        },
                        '& .MuiButton-startIcon': {
                            color: '#475467',
                        },
                        '& .MuiButton-endIcon': {
                            color: '#475467',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#ffffff',
                            borderColor: '#e2e8f0',
                            color: '#94a3b8',
                            '& .MuiButton-startIcon': {
                                color: '#94a3b8',
                            },
                        },
                    },
                    outlinedSecondary: {
                        backgroundColor: '#ffffff',
                        borderColor: secondary,
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        color: secondary,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: alpha(secondary, 0.04),
                            borderColor: secondary,
                            color: secondary,
                        },
                        '& .MuiButton-startIcon': {
                            color: secondary,
                        },
                        '& .MuiButton-endIcon': {
                            color: secondary,
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#ffffff',
                            borderColor: '#e2e8f0',
                            color: '#94a3b8',
                            '& .MuiButton-startIcon': {
                                color: '#94a3b8',
                            },
                        },
                    },
                    outlinedPrimary: {
                        backgroundColor: '#ffffff',
                        borderColor: primary,
                        color: primary,
                        fontWeight: 600,
                        '&:hover': {
                            backgroundColor: alpha(primary, 0.04),
                            borderColor: secondary,
                        },
                        '& .MuiButton-startIcon': {
                            color: primary,
                        },
                        '& .MuiButton-endIcon': {
                            color: primary,
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        backgroundColor: '#ffffff',
                        fontSize: '0.875rem',
                        color: '#314154',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#edf1f6',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#d0d5dd',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: primary,
                            borderWidth: '1.5px',
                        },
                    },
                    input: {
                        padding: '8.5px 14px',
                        '&::placeholder': {
                            color: '#98a0aa',
                            opacity: 1,
                        },
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        backgroundColor: '#ffffff',
                    },
                    select: {
                        paddingTop: '8.5px',
                        paddingBottom: '8.5px',
                        color: '#314154',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                    },
                    icon: {
                        color: '#98a0aa',
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        color: '#64748b',
                        '&:hover': {
                            backgroundColor: '#f8fafc',
                        },
                    },
                },
            },
            MuiInputAdornment: {
                styleOverrides: {
                    root: {
                        '& .MuiSvgIcon-root': {
                            color: '#98a0aa',
                            fontSize: '1.125rem',
                        },
                    },
                },
            },
            MuiTabs: {
                defaultProps: {
                    variant: 'scrollable',
                    scrollButtons: 'auto',
                    allowScrollButtonsMobile: true,
                },
                styleOverrides: {
                    root: {
                        minHeight: 44,
                        borderBottom: '1px solid #eaecf0',
                    },
                    indicator: {
                        backgroundColor: primary,
                        height: 2.5,
                        borderRadius: '2px 2px 0 0',
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        minWidth: 'auto',
                        minHeight: 44,
                        padding: '10px 20px',
                        color: '#64748b',
                        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                        '&.Mui-selected': {
                            color: primary,
                            fontWeight: 600,
                        },
                        '&:hover': {
                            color: '#1e293b',
                        },
                    },
                },
            },
            MuiTableContainer: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        border: '1px solid #eaecf0',
                        boxShadow: '0 1px 3px 0 rgba(16, 24, 40, 0.05), 0 1px 2px 0 rgba(16, 24, 40, 0.03)',
                        backgroundColor: '#ffffff',
                        overflowX: 'auto',
                        '&:has(tbody tr:only-child td[colspan]) .MuiTablePagination-root': {
                            display: 'none !important',
                        },
                        '&:has(tbody:empty) .MuiTablePagination-root': {
                            display: 'none !important',
                        },
                    },
                },
            },
            MuiTable: {
                styleOverrides: {
                    root: {
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                    },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#f8fafc',
                        '& .MuiTableCell-head': {
                            backgroundColor: '#f8fafc',
                            color: '#475467',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            letterSpacing: '0.02em',
                            textTransform: 'none',
                            borderBottom: '1px solid #eaecf0',
                            padding: '12px 16px',
                            whiteSpace: 'nowrap',
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderColor: '#f2f4f7',
                        padding: '14px 16px',
                        fontSize: '0.875rem',
                        color: '#1e293b',
                        verticalAlign: 'middle',
                    },
                    head: {
                        backgroundColor: '#f8fafc',
                        color: '#475467',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.02em',
                        textTransform: 'none',
                        borderBottom: '1px solid #eaecf0',
                        padding: '12px 16px',
                        whiteSpace: 'nowrap',
                        '& b, & strong': {
                            fontWeight: 600,
                            color: '#475467',
                        },
                    },
                    body: {
                        fontSize: '0.875rem',
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                            backgroundColor: '#f8fafc !important',
                        },
                        '&.Mui-selected': {
                            backgroundColor: alpha(primary, 0.04) + ' !important',
                            '&:hover': {
                                backgroundColor: alpha(primary, 0.07) + ' !important',
                            },
                        },
                    },
                },
            },
            MuiTablePagination: {
                defaultProps: {
                    ActionsComponent: TablePaginationActions,
                    labelDisplayedRows: ({ from, to, count }) =>
                        `Showing ${from}-${to} of ${count !== -1 ? count : `more than ${to}`} items`,
                },
                styleOverrides: {
                    root: {
                        borderTop: '1px solid #eaecf0',
                        color: '#475467',
                        backgroundColor: '#ffffff',
                    },
                    toolbar: {
                        minHeight: 56,
                        paddingLeft: 20,
                        paddingRight: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 16,
                    },
                    spacer: {
                        display: 'none',
                    },
                    displayedRows: {
                        color: '#475467',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        marginRight: 'auto',
                        order: -1,
                    },
                    selectLabel: {
                        color: '#475467',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                    },
                    select: {
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: '#344054',
                        borderRadius: 6,
                        border: '1px solid #eaecf0',
                        paddingTop: 4,
                        paddingBottom: 4,
                    },
                },
            },
        },
    });
};

